import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { ISongDetailsInput, ISongInputWrapper } from "../SongForm/SongForm";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import MessageBox from "../../MessageBox/MessageBox";
import Button from "@mui/material/Button/Button";
import ButtonGroup from "@mui/material/ButtonGroup/ButtonGroup";
import TextField from "@mui/material/TextField/TextField";
import "./Verse.css";

interface props {
  songNumber: number;
  id: number;
  input: ISongInputWrapper;
  setInput: React.Dispatch<React.SetStateAction<ISongInputWrapper[]>>;
  syncInputData: () => void;
  delete: (
    input: ISongInputWrapper,
    id: number,
    songNumber: number,
    type: "verse" | "chorus" | "melody"
  ) => void;
  first: boolean;
  fetchData: (isMounted: boolean, controller: AbortController) => Promise<void>;
}

const Verse = (props: props) => {
  const axiosPrivate = useAxiosPrivate();
  const bandId = sessionStorage.getItem("editedBandId");
  const VERSE_URL = bandId ? `/admin/stanzas/${bandId}` : "/stanzas";

  const [open, setOpen] = useState(false);
  const [result, setResult] = useState(true);
  const [newLyrics, setNewLyrics] = useState<string>(props.input.data.lyrics);
  const [newAcords, setNewAcords] = useState<string>(props.input.data.acords);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ISongDetailsInput>({
    defaultValues: {
      lyrics: "",
      acords: "",
    },
  });

  useEffect(() => {
    setValue("lyrics", props.input.data.lyrics);
    setValue("acords", props.input.data.acords);
  }, [props.input]);

  useEffect(() => {
    props.setInput((prev) => {
      props.input.data.acords = newAcords;
      props.input.data.lyrics = newLyrics;
      return prev;
    });
  }, [newLyrics, newAcords]);

  const onSubmit: SubmitHandler<ISongDetailsInput> = async (data) => {
    const { lyrics, acords } = data;

    console.log("Sending POST request to:", VERSE_URL);
    console.log("Request data:", {
      id: props.id,
      songNumber: props.songNumber,
      lyrics: lyrics,
      acords: acords,
    });

    try {
      const response = await axiosPrivate.post(
        VERSE_URL,
        JSON.stringify({
          id: props.id,
          songNumber: props.songNumber,
          lyrics: lyrics,
          acords: acords,
        }),
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      console.log("Response from API:", response);

      // Update the input data
      props.setInput((prev) => {
        props.input.data = data;
        return prev;
      });

      // Sync data and trigger fetchData
      props.syncInputData();
      setResult(true);

      const isMounted = true;
      const controller = new AbortController();
      props.fetchData(isMounted, controller);
    } catch (error) {
      console.error("Error during POST request:", error);
      setResult(false);
    }

    setOpen(true);
  };

  return (
    <form className="verse-container" onSubmit={handleSubmit(onSubmit)}>
      <div className="verse-content">
        <Controller
          name="lyrics"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <div style={{ width: "100%" }}>
              <label>* Zwrotka</label>
              <TextField
                {...field}
                error={newLyrics.length === 0 || newLyrics.trim() === ""}
                multiline
                rows={5}
                placeholder="* Wymagane"
                onChange={(e) => {
                  setNewLyrics(e.target.value);
                  field.onChange(e);
                }}
                value={newLyrics}
                helperText={errors.lyrics ? 'Wymagane!' : ''}
                sx={{
                  '& .MuiInputBase-input::placeholder': {
                    color: (theme) => theme.palette.error.main, // Kolor błędu z palety MUI
                    opacity: 1
                  }
                }}
              />
            </div>
          )}
        />
        <Controller
          name="acords"
          control={control}
          rules={{ required: props.first }}
          render={({ field }) => (
            <div style={{ width: "150px" }}>
              <label>* Chwyty</label>
              <TextField
                {...field}
                error={newAcords.length === 0 || newAcords.trim() === ""}
                multiline
                rows={5}
                placeholder={props.first ? "* Wymagane" : ""}
                onChange={(e) => {
                  setNewAcords(e.target.value);
                  field.onChange(e);
                }}
                value={newAcords}
                helperText={errors.acords ? 'Wymagane!' : ''}
                sx={{
                  '& .MuiInputBase-input::placeholder': {
                    color: (theme) => theme.palette.error.main, // Kolor błędu z palety MUI
                    opacity: 1
                  }
                }}
              />
            </div>
          )}
        />
      </div>
      <ButtonGroup
        className="verse-buttons"
        variant="contained"
        size="large"
        orientation="vertical"
      >
        <Button
          size="large"
          onClick={() =>
            props.delete(props.input, props.id, props.songNumber, "verse")
          }
        >
          USUŃ ZWROTKĘ
        </Button>
      </ButtonGroup>
      <MessageBox open={open} setOpen={setOpen} error={!result} />
    </form>
  );
};

export default Verse;
